import EventEmitter from 'events';
import { useEffect, memo } from 'react';
import { useSelector } from 'react-redux';
import pt from 'prop-types';

import toDate from 'date-fns/toDate';
import addMilliseconds from 'date-fns/addMilliseconds';
import startOfDay from 'date-fns/startOfDay';
import addMinutes from 'date-fns/addMinutes';

import { format } from '../utils/date-format';
import { useBool } from './hooks/bool';
import { withListener } from './hooks/sub-unsub';

const datetimeFormat = 'MMM d, yyyy HH:mm';
const datetimeFormatAmPm = 'MMM d, yyyy hh:mm a';
const dateOnlyFormat = 'MMM d, yyyy';

class Ticker extends EventEmitter {
  tick = () => this.emit('tick');

  constructor(interval) {
    super();
    this.setMaxListeners(0);
    this.once('newListener', () => setInterval(this.tick, interval));
    // Redraw all timers when document becomes visible
    typeof document !== 'undefined' &&
      document.addEventListener('visibilitychange', () => !document.hidden && this.tick());
  }
}

const ticker = new Ticker(30000); // 30 sec

const TimeDisplay = memo(function TimeDisplay({
  timeStamp,
  className,
  absolute,
  amPm,
  inline = false,
  dateOnly = false,
  children,
}) {
  const serverTimeAhead = useSelector((state) => state.serverTimeAhead);
  const prefsAmPm = useSelector((state) => state.user.frontendPreferences.timeDisplay.amPm);
  const prefsAbsolute = useSelector((state) => state.user.frontendPreferences.timeDisplay.absolute);
  const [, refresh] = useBool();

  useEffect(() => withListener(ticker, 'tick', refresh));

  const showAmPm = typeof amPm === 'boolean' ? amPm : prefsAmPm;
  const showAbsolute = typeof absolute === 'boolean' ? absolute : prefsAbsolute;

  const time = typeof timeStamp === 'number' ? toDate(timeStamp) : new Date(timeStamp);
  /**
   * TEST ENVIRONMENT ONLY HACK
   *
   * Due to a node.js bug (https://github.com/nodejs/node/issues/4230), the
   * TZ=UTC environment variable doesn't work on Windows. So we need to manually
   * convert the time to the UTC-like offset for the sake of the tests
   * stability.
   *
   * In the browser environment, the 'correctedTime' is the same object as the
   * original 'time'.
   */
  let correctedTime = time;
  if (process.platform === 'win32' && (process.env?.TZ || '').toUpperCase() === 'UTC') {
    correctedTime = addMinutes(time, time.getTimezoneOffset());
  }
  const serverNow = addMilliseconds(new Date(), serverTimeAhead);
  const timeRel = formatDistance(correctedTime, serverNow, { inline, amPm: showAmPm });
  const timeAbs = format(
    correctedTime,
    dateOnly ? dateOnlyFormat : showAmPm ? datetimeFormatAmPm : datetimeFormat,
  );
  const timeISO = time.toISOString();

  return (
    <time className={className} dateTime={timeISO} title={showAbsolute ? timeRel : timeAbs}>
      {children || (showAbsolute ? timeAbs : timeRel)}
    </time>
  );
});

TimeDisplay.propTypes = {
  timeStamp: pt.oneOfType([
    pt.number.isRequired, // JS timestamp in ms
    pt.string.isRequired, // ISO date string
  ]),
  className: pt.string,
  absolute: pt.bool,
  amPm: pt.bool,
  inline: pt.bool,
  dateOnly: pt.bool,
};

export default TimeDisplay;

/**
 * Date difference formatter
 */
export function formatDistance(date, now = new Date(), { amPm = false, inline = false } = {}) {
  const minutes = Math.floor((now - date) / (1000 * 60));
  if (minutes < 1) {
    return inline ? 'just now' : 'Just now';
  }
  if (minutes < 60) {
    return `${Math.floor(minutes)} ${inline ? 'min' : 'minutes'} ago`;
  }
  if (minutes < 1.5 * 60) {
    return `1 hour ago`;
  }
  if (minutes < 2 * 60) {
    return `1.5 hours ago`;
  }
  if (minutes < 4 * 60) {
    return `${Math.floor(minutes / 60)} hours ago`;
  }

  const dateNoon = startOfDay(date);
  const days = Math.floor((now - dateNoon) / (1000 * 3600 * 24));

  if (days <= 1) {
    const today = inline ? 'today ' : 'Today at ';
    const yesterday = inline ? 'yest. ' : 'Yesterday at ';
    const prefix = days === 1 ? yesterday : today;

    return prefix + format(date, amPm ? 'h:mm aaaa' : 'H:mm');
  }
  if (days < 4) {
    return `${days} days ago`;
  }
  if (now.getFullYear() === date.getFullYear()) {
    return format(date, 'LLL d');
  }
  return format(date, 'LLL d, y');
}
